/**
 * metricsMiddleware
 *
 * Uses RTK's createListenerMiddleware to intercept state actions and fire
 * metrics events as background fire-and-forget requests.
 *
 * Tracked events:
 *  - modal_open              – uiSlice.open() / uiSlice.toggle()
 *  - theme_changed           – updateUserInfo mutation fulfilled with { theme }
 *  - language_changed        – updateUserInfo mutation fulfilled with { language }
 *  - history_toggled         – uiSlice.toggle('history')
 *  - forecast_changed        – uiSlice.setForecast()
 *  - category_filter_changed – saveResourcesSettings mutation fulfilled
 *  - pdf_created             – dispatched directly from ScheduleModal
 */
import { createListenerMiddleware } from '@reduxjs/toolkit';
import { open, toggle, setForecast } from '@/store/uiSlice';
import { apiSlice } from '@/store/apiSlice';
import { ENDPOINTS } from '@/config/endpoints';

export const metricsListener = createListenerMiddleware();

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/';

function fireMetric(eventType: string, meta?: Record<string, unknown>) {
    // Fire-and-forget — never await, never throw
    fetch(API_BASE + ENDPOINTS.metricsEvents(), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: '*/*' },
        body: JSON.stringify({ eventType, meta }),
    }).catch(() => {
        /* swallow */
    });
}

// ── Modal opens ───────────────────────────────────────────────────────────────
metricsListener.startListening({
    actionCreator: open,
    effect: (action) => {
        fireMetric('modal_open', { modal: action.payload });
    },
});

metricsListener.startListening({
    actionCreator: toggle,
    effect: (action) => {
        // Track history toggle separately for dedicated metric
        if (action.payload === 'history') {
            fireMetric('history_toggled', { modal: 'history' });
        } else {
            fireMetric('modal_open', { modal: action.payload });
        }
    },
});

// ── Forecast changes ──────────────────────────────────────────────────────────
metricsListener.startListening({
    actionCreator: setForecast,
    effect: (action) => {
        fireMetric('forecast_changed', { value: action.payload });
    },
});

// ── Theme & language changes (updateUserInfo mutation) ───────────────────────
metricsListener.startListening({
    matcher: apiSlice.endpoints.updateUserInfo.matchFulfilled,
    effect: (_action, _listenerApi) => {
        const arg = (_action as any).meta?.arg?.originalArgs;
        if (arg && typeof arg === 'object') {
            if ('theme' in arg) {
                fireMetric('theme_changed', { theme: (arg as { theme: string }).theme });
            }
            if ('language' in arg) {
                fireMetric('language_changed', {
                    language: (arg as { language: string }).language,
                });
            }
        }
    },
});

// ── Category filter saves ─────────────────────────────────────────────────────
metricsListener.startListening({
    matcher: apiSlice.endpoints.saveResourcesSettings.matchFulfilled,
    effect: (_action) => {
        const arg = (_action as any).meta?.arg?.originalArgs;
        const hidden: string[] = arg?.hiddenCategories ?? [];
        fireMetric('category_filter_changed', { hiddenCount: hidden.length });
    },
});
